import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, TrendingUp, BarChart3, Bell, Loader } from 'lucide-react';

const BENEFITS = [
  {
    icon: TrendingUp,
    title: 'Market Insights',
    description: 'Weekly summaries of key market moves, sector trends, and macro themes that matter for your portfolio.',
  },
  {
    icon: BarChart3,
    title: 'Research Highlights',
    description: 'Curated stock ideas, signal updates, and research tips to help you get more from Bidsig\'s tools.',
  },
  {
    icon: Bell,
    title: 'Product Updates',
    description: 'Be the first to know about new features for watchlists, portfolios, alerts, and AI-powered research.',
  },
];

const Newsletter = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formSuccess, setFormSuccess] = useState(null);
  const [formError, setFormError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setFormSuccess(null);
    setSubmitting(true);

    try {
      const res = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          source: 'newsletter_page',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to subscribe. Please try again.');
      setFormSuccess(data.message || 'Thank you for subscribing! Check your inbox to confirm your subscription.');
      setEmail('');
    } catch (err) {
      setFormError(err.message || 'Something went wrong. Please try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-content info-page newsletter-page">
      <header className="info-page-header">
        <h2>
          <Mail size={22} className="info-page-header-icon" aria-hidden />
          Newsletter
        </h2>
      </header>

      <div className="info-hero-card">
        <div className="info-hero-body">
          <p className="info-hero-lead">
            Stay ahead with curated market insights and product updates — built for investors who research and track portfolios with Bidsig. No spam, unsubscribe anytime.
          </p>
        </div>
      </div>

      <section className="info-section" aria-labelledby="newsletter-benefits-title">
        <h2 id="newsletter-benefits-title" className="info-section-title">What You&apos;ll Receive</h2>
        <p className="info-section-desc">
          A concise, actionable digest delivered to your inbox — focused on the information that helps you make better investment decisions.
        </p>
        <div className="info-feature-grid info-feature-grid--3">
          {BENEFITS.map(({ icon: Icon, title, description }) => (
            <article key={title} className="info-card info-feature-card">
              <div className="info-card-icon info-card-icon--newsletter" aria-hidden>
                <Icon size={22} />
              </div>
              <h3 className="info-card-title">{title}</h3>
              <p className="info-card-text">{description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="info-section" aria-labelledby="newsletter-subscribe-title">
        <h2 id="newsletter-subscribe-title" className="info-section-title">Subscribe</h2>
        <p className="info-section-desc">
          Enter your email below to join our mailing list. We typically send one email per week.
        </p>

        <form className="newsletter-subscribe-form info-data-card" onSubmit={handleSubmit} noValidate>
          {formSuccess && (
            <div className="support-form-banner support-form-banner--success" role="status">
              {formSuccess}
            </div>
          )}
          {formError && (
            <div className="support-form-banner support-form-banner--error" role="alert">
              {formError}
            </div>
          )}

          <div className="newsletter-form-row">
            <label className="support-contact-field newsletter-email-field">
              <span>Email address</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="email"
                aria-label="Email address"
              />
            </label>
            <div className="newsletter-form-actions">
              <button
                type="submit"
                className="pw-btn pw-btn-primary newsletter-submit-btn"
                disabled={submitting}
              >
                {submitting ? <Loader size={16} className="spinning" aria-hidden /> : <Mail size={16} aria-hidden />}
                {submitting ? 'Subscribing…' : 'Subscribe'}
              </button>
            </div>
          </div>

          <p className="newsletter-privacy-note">
            We respect your privacy. Your email is used only for newsletter delivery and will never be sold or shared with third parties.
          </p>
        </form>
      </section>

      <div className="info-closing-card">
        <p>
          Prefer to explore on your own? Browse our <Link to="/learn">Knowledge Center</Link> for guides on watchlists, portfolios, signals, and more.
        </p>
      </div>
    </div>
  );
};

export default Newsletter;
